import { useEffect, useMemo, useState } from "react";
import {
  createProperty,
  deleteProperty,
  fetchProperties,
} from "../../../store/slices/propertySlice";
import {
  createUnit,
  deleteUnit,
  fetchUnits,
  updateUnit,
} from "../../../store/slices/unitSlice";
import { loadCurrentUser } from "../../../store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { CreatePropertyRequest } from "../../../models/RequestModels/CreatePropertyRequest";
import { CreateUnitRequest } from "../../../models/RequestModels/CreateUnitRequest";
import { PropertyResponse } from "../../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../../models/ResponseModels/UnitResponse";
import {
  getApiErrorMessage,
  isSubscriptionLimitPayload,
} from "../../../utils/helperFunctions/apiErrorHelper";

export const usePropertiesScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { properties, loading: propertiesLoading } = useAppSelector(
    (state) => state.properties,
  );
  const { units, loading: unitsLoading } = useAppSelector(
    (state) => state.units,
  );

  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [unitModalOpen, setUnitModalOpen] = useState(false);

  const [selectedProperty, setSelectedProperty] =
    useState<PropertyResponse | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitResponse | null>(null);

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  useEffect(() => {
    dispatch(fetchProperties());
    dispatch(fetchUnits());
  }, [dispatch]);

  const loading = propertiesLoading || unitsLoading;

  const activeUnits = useMemo(() => {
    return units.filter((unit) => unit.active);
  }, [units]);

  const inactiveUnits = useMemo(() => {
    return units.filter((unit) => !unit.active);
  }, [units]);

  const getUnitsForProperty = (propertyId: number) => {
    return units.filter((unit) => unit.property_id === propertyId);
  };

  const getActiveUnitsForProperty = (propertyId: number) => {
    return activeUnits.filter((unit) => unit.property_id === propertyId);
  };

  const getInactiveUnitsForProperty = (propertyId: number) => {
    return inactiveUnits.filter((unit) => unit.property_id === propertyId);
  };

  const openPropertyModal = () => {
    setPropertyModalOpen(true);
  };

  const closePropertyModal = () => {
    setPropertyModalOpen(false);
  };

  const openUnitModal = (property?: PropertyResponse) => {
    setSelectedProperty(property || null);
    setSelectedUnit(null);
    setUnitModalOpen(true);
  };

  const openEditUnitModal = (unit: UnitResponse) => {
    const property =
      properties.find(
        (currentProperty) => currentProperty.id === unit.property_id,
      ) || null;

    setSelectedProperty(property);
    setSelectedUnit(unit);
    setUnitModalOpen(true);
  };

  const closeUnitModal = () => {
    setUnitModalOpen(false);
    setSelectedProperty(null);
    setSelectedUnit(null);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
    setUpgradeMessage("");
  };

  const showErrorModal = (message: string) => {
    setUpgradeMessage(message);
    setUpgradeModalOpen(true);
  };

  const submitProperty = async (body: CreatePropertyRequest) => {
    const result = await dispatch(createProperty(body));

    if (createProperty.fulfilled.match(result)) {
      closePropertyModal();
      dispatch(loadCurrentUser());
      return;
    }

    if (isSubscriptionLimitPayload(result.payload)) {
      closePropertyModal();
      showErrorModal(
        getApiErrorMessage(
          result.payload,
          t("subscription.upgradeRequiredMessage"),
        ),
      );
      return;
    }

    showErrorModal(
      getApiErrorMessage(result.payload, t("properties.failedCreateProperty")),
    );
  };

  const submitUnit = async (body: CreateUnitRequest) => {
    const result = selectedUnit
      ? await dispatch(
          updateUnit({
            id: selectedUnit.id,
            body,
          }),
        )
      : await dispatch(createUnit(body));

    if (
      createUnit.fulfilled.match(result) ||
      updateUnit.fulfilled.match(result)
    ) {
      closeUnitModal();
      dispatch(loadCurrentUser());
      return;
    }

    if (isSubscriptionLimitPayload(result.payload)) {
      closeUnitModal();
      showErrorModal(
        getApiErrorMessage(
          result.payload,
          t("subscription.upgradeRequiredMessage"),
        ),
      );
      return;
    }

    showErrorModal(
      getApiErrorMessage(result.payload, t("properties.failedCreateUnit")),
    );
  };

  const toggleUnitActive = async (unit: UnitResponse) => {
    const result = await dispatch(
      updateUnit({
        id: unit.id,
        body: {
          property_id: unit.property_id,
          name: unit.name,
          type: unit.type,
          capacity: unit.capacity,
          default_price_per_night: unit.default_price_per_night
            ? Number(unit.default_price_per_night)
            : null,
          active: !unit.active,
          default_cleaner_id: unit.default_cleaner_id,
        },
      }),
    );

    if (!updateUnit.fulfilled.match(result)) {
      showErrorModal(
        getApiErrorMessage(result.payload, t("properties.failedUpdateUnit")),
      );
    }
  };

  const confirmDeleteUnit = async (unit: UnitResponse) => {
    if (!window.confirm(`Delete ${unit.name}?`)) {
      return;
    }

    const result = await dispatch(deleteUnit(unit.id));

    if (!deleteUnit.fulfilled.match(result)) {
      showErrorModal(
        getApiErrorMessage(result.payload, t("properties.failedDeleteUnit")),
      );
    }
  };

  const confirmDeleteProperty = async (property: PropertyResponse) => {
    const propertyUnits = getUnitsForProperty(property.id);

    const confirmed = window.confirm(
      propertyUnits.length > 0
        ? `Delete ${property.name}? This property has ${propertyUnits.length} unit(s).`
        : `Delete ${property.name}?`,
    );

    if (!confirmed) {
      return;
    }

    const result = await dispatch(deleteProperty(property.id));

    if (deleteProperty.fulfilled.match(result)) {
      dispatch(fetchUnits());
      dispatch(loadCurrentUser());
      return;
    }

    showErrorModal(
      getApiErrorMessage(result.payload, t("properties.failedDeleteProperty")),
    );
  };

  const refreshData = () => {
    dispatch(fetchProperties());
    dispatch(fetchUnits());
  };

  return {
    t,

    properties,
    units,
    activeUnits,
    inactiveUnits,
    loading,

    getUnitsForProperty,
    getActiveUnitsForProperty,
    getInactiveUnitsForProperty,

    propertyModalOpen,
    openPropertyModal,
    closePropertyModal,
    submitProperty,

    unitModalOpen,
    selectedProperty,
    selectedUnit,
    openUnitModal,
    openEditUnitModal,
    closeUnitModal,
    submitUnit,
    toggleUnitActive,
    confirmDeleteUnit,
    confirmDeleteProperty,

    upgradeModalOpen,
    upgradeMessage,
    closeUpgradeModal,
    refreshData,
  };
};
